import React, { useState, useEffect } from 'react';
import { ClipboardList, Plus, Edit, Trash2, Download, RefreshCw, PlusCircle, MinusCircle, Settings, List } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const Questionnaires = () => {
  const { filialeId } = useFiliale();
  const [data, setData] = useState([]);
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    offre_id: '',
    duree_minutes: 30,
    note_passage: 60,
    auto_correction: true,
    afficher_score: true,
    actif: true,
    questions: []
  });

  const QUESTION_TYPES = [
    { value: 'text', label: 'Texte court' },
    { value: 'textarea', label: 'Texte long' },
    { value: 'radio', label: 'Choix unique' },
    { value: 'checkbox', label: 'Choix multiple' },
    { value: 'select', label: 'Liste déroulante' },
    { value: 'number', label: 'Nombre' },
    { value: 'email', label: 'Email' }
  ];

  const tabs = [
    { id: 'general', label: 'Général', icon: Settings },
    { id: 'questions', label: 'Questions', icon: List }
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = filialeId && filialeId !== 'all' ? { filiale_id: filialeId } : {};
      const response = await http.get('/api/questionnaires', { params });
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur questionnaires:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOffres = async () => {
    try {
      const params = filialeId && filialeId !== 'all' ? { filiale_id: filialeId } : {};
      const response = await http.get('/api/offres', { params });
      setOffres(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur offres:', error);
      setOffres([]);
    }
  };

  useEffect(() => {
    fetchData();
    fetchOffres();
  }, [filialeId]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      titre: '',
      description: '',
      offre_id: '',
      duree_minutes: 30,
      note_passage: 60,
      auto_correction: true,
      afficher_score: true,
      actif: true,
      questions: []
    });
    setActiveTab('general');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      titre: item.titre,
      description: item.description || '',
      offre_id: item.offre_id || '',
      duree_minutes: item.duree_minutes || 30,
      note_passage: item.note_passage || 60,
      auto_correction: item.auto_correction === 1,
      afficher_score: item.afficher_score === 1,
      actif: item.actif === 1,
      questions: item.questions || []
    });
    setActiveTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce questionnaire et toutes ses réponses ?')) return;
    try {
      await http.delete(`/api/questionnaires/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        offre_id: formData.offre_id || null,
        filiale_id: filialeId !== 'all' ? filialeId : undefined
      };

      if (editingItem) {
        await http.put(`/api/questionnaires/${editingItem.id}`, payload);
      } else {
        await http.post('/api/questionnaires', payload);
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur enregistrement');
    }
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [
        ...formData.questions,
        {
          question: '',
          type: 'text',
          required: true,
          auto_grade: false,
          expected_answer: '',
          options: []
        }
      ]
    });
  };

  const removeQuestion = (index) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter((_, i) => i !== index)
    });
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index][field] = value;
    setFormData({ ...formData, questions: newQuestions });
  };

  const addOption = (questionIndex) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].options = [...newQuestions[questionIndex].options, ''];
    setFormData({ ...formData, questions: newQuestions });
  };

  const removeOption = (questionIndex, optionIndex) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].options = newQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
    setFormData({ ...formData, questions: newQuestions });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].options[optionIndex] = value;
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleExportCSV = async (id) => {
    try {
      const response = await http.get(`/api/questionnaires/${id}/export-csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `questionnaire_${id}.csv`;
      link.click();
    } catch (error) {
      alert('Erreur export CSV');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Questionnaires"
        subtitle="Tests en ligne pour les candidats"
        icon={ClipboardList}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleCreate, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false }
        ]}
      />

      <div className="px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-gray-500">Chargement…</div>
          ) : data.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-gray-500">Aucun questionnaire. Créez-en un pour démarrer.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Offre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durée</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note passage</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Auto-correction</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{item.titre}</div>
                        {item.description && <div className="text-xs text-gray-500">{item.description}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.offre_titre || 'Générique'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.nb_questions || 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.duree_minutes} min</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.note_passage}%</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${item.auto_correction ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {item.auto_correction ? 'Oui' : 'Non'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${item.actif ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                          {item.actif ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800" title="Modifier">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800" title="Supprimer">
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleExportCSV(item.id)} className="text-green-600 hover:text-green-800" title="Export CSV">
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-lg font-bold text-blue-900">{editingItem ? 'Modifier' : 'Créer'} un questionnaire</h3>
              <button type="button" onClick={() => setShowModal(false)}>✕</button>
            </div>

            {/* Navigation par onglets */}
            <nav className="flex gap-1 overflow-x-auto border-b px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2.5 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-blue-600 text-blue-700'
                        : 'border-transparent text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              {activeTab === 'general' && (
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium">Titre *</label>
                      <input className="input-field w-full" value={formData.titre} onChange={(e) => setFormData({ ...formData, titre: e.target.value })} required />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Offre associée</label>
                      <select className="input-field w-full" value={formData.offre_id} onChange={(e) => setFormData({ ...formData, offre_id: e.target.value })}>
                        <option value="">Générique (pas d'offre)</option>
                        {offres.map((o) => <option key={o.id} value={o.id}>{o.titre}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Durée (minutes) *</label>
                      <input type="number" className="input-field w-full" value={formData.duree_minutes} onChange={(e) => setFormData({ ...formData, duree_minutes: parseInt(e.target.value) })} min="5" max="180" required />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Note de passage (%)</label>
                      <input type="number" className="input-field w-full" value={formData.note_passage} onChange={(e) => setFormData({ ...formData, note_passage: parseFloat(e.target.value) })} min="0" max="100" step="0.1" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium">Description</label>
                      <textarea className="input-field w-full" rows="2" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                    </div>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={formData.auto_correction} onChange={(e) => setFormData({ ...formData, auto_correction: e.target.checked })} />
                        <span className="text-sm">Auto-correction</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={formData.afficher_score} onChange={(e) => setFormData({ ...formData, afficher_score: e.target.checked })} />
                        <span className="text">Afficher score au candidat</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={formData.actif} onChange={(e) => setFormData({ ...formData, actif: e.target.checked })} />
                        <span className="text-sm">Actif</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'questions' && (
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium">Questions ({formData.questions.length})</h4>
                    <button type="button" onClick={addQuestion} className="px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700">
                      <PlusCircle className="w-4 h-4 inline mr-1" /> Ajouter
                    </button>
                  </div>

                  {formData.questions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">Aucune question ajoutée</div>
                  ) : (
                    <div className="space-y-4">
                      {formData.questions.map((q, index) => (
                        <div key={index} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-start mb-3">
                            <span className="font-medium text-blue-900">Question {index + 1}</span>
                            <button type="button" onClick={() => removeQuestion(index)} className="text-red-600 hover:text-red-800">
                              <MinusCircle className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div className="md:col-span-2">
                              <label className="mb-1 block text-xs font-medium">Texte de la question *</label>
                              <input className="input-field w-full text-sm" value={q.question} onChange={(e) => updateQuestion(index, 'question', e.target.value)} required />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium">Type *</label>
                              <select className="input-field w-full text-sm" value={q.type} onChange={(e) => updateQuestion(index, 'type', e.target.value)} required>
                                {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                            </div>
                            <div className="flex items-center gap-2">
                              <input type="checkbox" checked={q.required} onChange={(e) => updateQuestion(index, 'required', e.target.checked)} />
                              <span className="text-xs">Obligatoire</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <input type="checkbox" checked={q.auto_grade} onChange={(e) => updateQuestion(index, 'auto_grade', e.target.checked)} />
                              <span className="text-xs">Auto-correction</span>
                            </div>
                          </div>

                          {q.auto_grade && (
                            <div className="mb-3">
                              <label className="mb-1 block text-xs font-medium">Réponse attendue *</label>
                              {['radio', 'select'].includes(q.type) ? (
                                <select className="input-field w-full text-sm" value={q.expected_answer} onChange={(e) => updateQuestion(index, 'expected_answer', e.target.value)}>
                                  <option value="">Choisir la bonne réponse</option>
                                  {q.options.map((opt, oi) => <option key={oi} value={opt}>{opt}</option>)}
                                </select>
                              ) : ['checkbox'].includes(q.type) ? (
                                <div className="space-y-1">
                                  {q.options.map((opt, oi) => (
                                    <label key={oi} className="flex items-center gap-2 text-sm">
                                      <input
                                        type="checkbox"
                                        checked={Array.isArray(q.expected_answer) && q.expected_answer.includes(opt)}
                                        onChange={(e) => {
                                          const current = q.expected_answer || [];
                                          if (e.target.checked) {
                                            updateQuestion(index, 'expected_answer', [...current, opt]);
                                          } else {
                                            updateQuestion(index, 'expected_answer', current.filter((x) => x !== opt));
                                          }
                                        }}
                                      />
                                      {opt}
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <input className="input-field w-full text-sm" value={q.expected_answer} onChange={(e) => updateQuestion(index, 'expected_answer', e.target.value)} />
                              )}
                            </div>
                          )}

                          {['radio', 'checkbox', 'select'].includes(q.type) && (
                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <label className="mb-1 block text-xs font-medium">Options de réponse</label>
                                <button type="button" onClick={() => addOption(index)} className="text-xs text-blue-600 hover:text-blue-800">
                                  + Ajouter
                                </button>
                              </div>
                              {q.options.map((opt, oi) => (
                                <div key={oi} className="flex gap-2 mb-2">
                                  <input className="input-field flex-1 text-sm" value={opt} onChange={(e) => updateOption(index, oi, e.target.value)} />
                                  <button type="button" onClick={() => removeOption(index, oi)} className="text-red-600">
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 border-t px-6 py-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-200 rounded-md">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  {editingItem ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Questionnaires;
